import React from "react";
import { Button } from "@akuna/ui";
import { captureException } from "@/lib/monitoring";

type DOMErrorBoundaryProps = {
  children: React.ReactNode;
};

type DOMErrorBoundaryState = {
  hasError: boolean;
  resetKey: number;
  recoverAttempts: number;
  lastErrorMessage?: string;
};

const MAX_AUTO_RECOVER_ATTEMPTS = 1;
const WHATSAPP_SUPPORT_BASE_URL = "https://wa.me/5511987679777";

export class DOMErrorBoundary extends React.Component<DOMErrorBoundaryProps, DOMErrorBoundaryState> {
  state: DOMErrorBoundaryState = {
    hasError: false,
    resetKey: 0,
    recoverAttempts: 0,
  };

  static getDerivedStateFromError(error: unknown) {
    return {
      hasError: true,
      lastErrorMessage: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: unknown) {
    console.error(error);
    void captureException(error, {
      level: "fatal",
      tags: { boundary: "DOMErrorBoundary" },
    });
    if (this.state.recoverAttempts >= MAX_AUTO_RECOVER_ATTEMPTS) return;

    window.setTimeout(() => {
      this.setState((prev) => ({
        hasError: false,
        resetKey: prev.resetKey + 1,
        recoverAttempts: prev.recoverAttempts + 1,
      }));
    }, 0);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const supportMessage = [
        "Olá! Preciso de suporte no Akuna.",
        this.state.lastErrorMessage ? `Erro: ${this.state.lastErrorMessage}` : null,
        typeof window !== "undefined" ? `URL: ${window.location.href}` : null,
      ]
        .filter(Boolean)
        .join("\n");
      const supportHref = `${WHATSAPP_SUPPORT_BASE_URL}?text=${encodeURIComponent(supportMessage)}`;

      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
          <div className="max-w-lg space-y-4 rounded-card border bg-card p-6 shadow-soft-card">
            <h1 className="text-xl font-semibold text-foreground">Ops, algo saiu do esperado.</h1>
            <p className="text-sm text-muted-foreground">
              Ocorreu um erro interno inesperado. Recarregue a página para tentar novamente ou entre em contato com o
              suporte.
            </p>
            {this.state.lastErrorMessage && (
              <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
                {this.state.lastErrorMessage}
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button variant="outline" asChild>
                <a href={supportHref} target="_blank" rel="noreferrer">
                  Falar com o suporte
                </a>
              </Button>
              <Button onClick={this.handleReload}>Recarregar página</Button>
            </div>
          </div>
        </div>
      );
    }

    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}
